import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Star, Send, CheckCircle } from 'lucide-react';
import { colors } from '../theme';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PesquisaSatisfacao = () => {
  const { pedidoId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [jaRespondeu, setJaRespondeu] = useState(false);
  const [pedido, setPedido] = useState(null);
  
  const [avaliacao, setAvaliacao] = useState({
    produtos: 0,
    atendimento: 0,
    prazo: 0,
    embalagem: 0,
    comentario: ''
  });

  useEffect(() => {
    verificarPedido();
  }, [pedidoId]);

  const verificarPedido = async () => {
    try {
      const response = await axios.get(`${API}/feedback/pedido/${pedidoId}`);
      setPedido(response.data.pedido);
      setJaRespondeu(response.data.ja_respondeu);
    } catch (err) {
      setError('Pedido não encontrado ou link inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleStarClick = (categoria, valor) => {
    setAvaliacao(prev => ({ ...prev, [categoria]: valor }));
  };

  const renderEstrelas = (categoria) => {
    const avaliacaoAtual = avaliacao[categoria];
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(valor => (
          <button
            key={valor}
            type="button"
            onClick={() => handleStarClick(categoria, valor)}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            className="transition-transform cursor-pointer"
          >
            <Star
              size={40}
              fill={valor <= avaliacaoAtual ? '#FCD34D' : 'none'}
              stroke={valor <= avaliacaoAtual ? '#FCD34D' : '#D1D5DB'}
              strokeWidth={2}
            />
          </button>
        ))}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar se todas as avaliações foram dadas
    if (!avaliacao.produtos || !avaliacao.atendimento || !avaliacao.prazo || !avaliacao.embalagem) {
      setError('Por favor, avalie todos os itens');
      return;
    }

    setSending(true);
    setError('');

    try {
      await axios.post(`${API}/feedback`, {
        pedido_id: pedidoId,
        avaliacao_produtos: avaliacao.produtos,
        avaliacao_atendimento: avaliacao.atendimento,
        avaliacao_prazo: avaliacao.prazo,
        avaliacao_embalagem: avaliacao.embalagem,
        comentario: avaliacao.comentario || null
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar feedback');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: colors.primaza.primary }} />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="mb-6">
              <CheckCircle size={80} className="mx-auto" style={{ color: colors.primaza.primary }} />
            </div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: colors.primaza.secondary }}>
              Obrigado pelo seu feedback!
            </h2>
            <p className="text-gray-600 mb-6">
              Sua opinião é muito importante para nós e nos ajuda a melhorar continuamente nossos serviços.
            </p>
            <p className="text-lg font-semibold" style={{ color: colors.primaza.primary }}>
              PRIMAZA agradece! 💚
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (jaRespondeu) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <h2 className="text-2xl font-bold mb-4" style={{ color: colors.primaza.secondary }}>
              Pesquisa já respondida
            </h2>
            <p className="text-gray-600">
              Você já respondeu a pesquisa de satisfação para este pedido.
            </p>
            <p className="text-gray-600 mt-4">
              Obrigado pelo seu feedback!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: colors.primaza.secondary }}>
            PRIMAZA
          </h1>
          <p className="text-gray-600">Sistema de Rastreamento</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              <Star className="inline-block mr-2" style={{ color: '#FCD34D' }} size={32} />
              Como foi sua experiência?
            </CardTitle>
            <p className="text-center text-gray-600 mt-2">
              Pedido <strong>#{pedido?.pedido}</strong>
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Qualidade dos Produtos */}
              <div>
                <label className="block text-lg font-semibold mb-3">
                  📦 Qualidade dos Produtos
                </label>
                <div className="flex justify-center">
                  {renderEstrelas('produtos')}
                </div>
              </div>

              {/* Atendimento */}
              <div>
                <label className="block text-lg font-semibold mb-3">
                  🤝 Atendimento
                </label>
                <div className="flex justify-center">
                  {renderEstrelas('atendimento')}
                </div>
              </div>

              {/* Prazo de Entrega */}
              <div>
                <label className="block text-lg font-semibold mb-3">
                  ⏱️ Prazo de Entrega
                </label>
                <div className="flex justify-center">
                  {renderEstrelas('prazo')}
                </div>
              </div>

              {/* Embalagem */}
              <div>
                <label className="block text-lg font-semibold mb-3">
                  📦 Embalagem
                </label>
                <div className="flex justify-center">
                  {renderEstrelas('embalagem')}
                </div>
              </div>

              {/* Comentário */}
              <div>
                <label className="block text-lg font-semibold mb-3">
                  💬 Comentários (opcional)
                </label>
                <Textarea
                  rows={4}
                  placeholder="Conte-nos mais sobre sua experiência..."
                  value={avaliacao.comentario}
                  onChange={(e) => setAvaliacao(prev => ({ ...prev, comentario: e.target.value }))}
                  className="w-full"
                />
              </div>

              {/* Alertas */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Botão */}
              <div className="text-center">
                <Button
                  type="submit"
                  disabled={sending}
                  size="lg"
                  className="w-full md:w-auto"
                  style={{ backgroundColor: colors.primaza.primary }}
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Enviar Avaliação
                    </>
                  )}
                </Button>
              </div>

            </form>
          </CardContent>
        </Card>

        {/* Rodapé */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} PRIMAZA - Todos os direitos reservados</p>
        </div>
      </div>
    </div>
  );
};

export default PesquisaSatisfacao;
